// ─────────────────────────────────────────────────────────────────────────────
// useMarketoFormTracking
//
// dataLayer-Pushes für Marketo-Formulare (Contact Funnel, Application Funnel,
// Gated Content). Konzept: .claude/tasks/MARKETO_MULTISTEP_TRACKING_KONZEPT.md
//
// Nicht zutreffende Keys werden pro Push explizit auf undefined gesetzt, damit
// im GTM-Data-Model keine Werte vorheriger Pushes (z.B. eines zweiten
// Formulars auf derselben Seite) "durchsickern".
// ─────────────────────────────────────────────────────────────────────────────

/** Section-Fallback für Multistep-Formulare ohne explizites funnel-section */
export const DEFAULT_FUNNEL_SECTION = "Contact Funnel";

export type FormSubmitStatus = "Successful" | "Unsuccessful";

const CONTACT_EVENT = "Contact_Related_Interaction";
const VALIDATION_ERROR_EVENT = "form_validation_error";

const push = (payload: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const w = window as unknown as { dataLayer?: unknown[] };
    (w.dataLayer = w.dataLayer || []).push(payload);
};

/** Akkordeon geöffnet (nur per User-Klick, z.B. ContactSlider) */
export const pushBeginContentExpanded = (section: string) =>
    push({
        event: CONTACT_EVENT,
        section,
        action: "Begin Content Expanded",
        raw_action: "Click",
        step: "1",
        theme: undefined,
        inquiry: undefined,
        status: undefined,
        company_input: undefined,
        fields: undefined,
    });

/** Rolle gewählt (Multistep-Step 1 / ZformContactType) */
export const pushThemeSelected = (section: string, theme: string) =>
    push({
        event: CONTACT_EVENT,
        section,
        action: "Theme Selected",
        raw_action: "Click",
        step: "2",
        theme,
        inquiry: undefined,
        status: undefined,
        company_input: undefined,
        fields: undefined,
    });

/** Thema gewählt (Multistep-Step 2 / requestTopic) */
export const pushInquirySelected = (section: string, inquiry: string) =>
    push({
        event: CONTACT_EVENT,
        section,
        action: "Inquiry Selected",
        raw_action: "Click",
        step: "3",
        inquiry,
        status: undefined,
        company_input: undefined,
        fields: undefined,
    });

/**
 * Formular abgesendet: Erfolg oder technischer Fehler.
 * keepSelection: im Multistep bleiben theme/inquiry erhalten, damit die
 * Kombination Rolle/Thema/Submit auswertbar bleibt.
 */
export const pushFormSubmitted = (
    section: string,
    status: FormSubmitStatus,
    companyInput: string | undefined,
    step: string,
    keepSelection = false
) =>
    push({
        event: CONTACT_EVENT,
        section,
        action: "Form Submitted",
        raw_action: "Visibility",
        step,
        status,
        company_input: companyInput,
        fields: undefined,
        ...(keepSelection ? {} : { theme: undefined, inquiry: undefined }),
    });

/**
 * Inhaltlicher Validierungsfehler – ein Push pro Submit-Versuch.
 * keepSelection: wie bei pushFormSubmitted, sonst gingen theme/inquiry für
 * den folgenden erfolgreichen Submit verloren.
 */
export const pushFormValidationError = (
    section: string,
    invalidFieldLabels: string[],
    step: string,
    keepSelection = false
) =>
    push({
        event: VALIDATION_ERROR_EVENT,
        section,
        step,
        // Komma-getrennter String statt Array (GTM merged Arrays per Index,
        // GA4-Event-Parameter können keine Arrays aufnehmen)
        fields: invalidFieldLabels.join(", "),
        status: undefined,
        company_input: undefined,
        ...(keepSelection ? {} : { theme: undefined, inquiry: undefined }),
    });

export const useMarketoFormTracking = () => ({
    pushBeginContentExpanded,
    pushThemeSelected,
    pushInquirySelected,
    pushFormSubmitted,
    pushFormValidationError,
});
